<script setup lang="ts">
  defineOptions({ name: 'EventsDetailSectionSkeleton' })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import CPSkeleton from '@ui/components/CPSkeleton.vue'

  const props = defineProps({
    gap: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      default: 'md',
    },
    showAbout: {
      type: Boolean,
      default: true,
    },
    showAgenda: {
      type: Boolean,
      default: true,
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const { t } = useI18n()

  const gapClass = computed(() => {
    const map = { sm: 'gap-2', md: 'gap-4', lg: 'gap-6' } as const
    return map[props.gap]
  })
</script>

<template>
  <div
    :id="createElementId(idPrefix, 'section', 'skeleton-root')"
    class="flex w-full flex-col"
    role="status"
    aria-busy="true"
    :aria-label="t('clientportal.events.detail.loadingTitle')"
  >
    <div class="mx-auto w-full max-w-[82.5rem] px-4 pt-6 md:px-6">
      <div class="relative aspect-[2/1] w-full overflow-hidden rounded-xl">
        <CPSkeleton :id="createElementId(idPrefix, 'skeleton', 'banner')" type="image" width="100%" height="100%" :round="false" />
      </div>
    </div>

    <div class="sticky top-0 z-20 mt-5 w-full border-b border-[var(--cp-border-200)] bg-surface">
      <div class="mx-auto flex h-12 w-full max-w-[82.5rem] items-center gap-6 px-4 md:px-6">
        <CPSkeleton
          v-for="index in 4"
          :id="createElementId(idPrefix, 'skeleton-tab', String(index))"
          :key="index"
          type="text"
          width="5rem"
          height="1rem"
        />
      </div>
    </div>

    <div class="mx-auto w-full max-w-[82.5rem] px-4 py-6 md:px-6">
      <div :id="createElementId(idPrefix, 'skeleton', 'layout')" class="flex min-w-0 flex-col" :class="gapClass">
        <section
          v-if="showAbout"
          :id="createElementId(idPrefix, 'skeleton', 'about')"
          class="flex flex-col gap-4 rounded-xl border border-solid border-[var(--cp-border-200)] bg-background p-6 shadow-sm"
        >
          <CPSkeleton :id="createElementId(idPrefix, 'skeleton-about', 'title')" type="text" width="16rem" height="2rem" />
          <div class="grid grid-cols-2 gap-4 border-y border-solid border-[var(--cp-border-200)] py-5 sm:grid-cols-4">
            <div
              v-for="index in 4"
              :id="createElementId(idPrefix, 'skeleton-stat', String(index))"
              :key="index"
              class="flex flex-col gap-2"
            >
              <CPSkeleton type="text" width="3rem" height="0.75rem" />
              <CPSkeleton type="text" width="5rem" height="1rem" />
            </div>
          </div>
          <CPSkeleton :id="createElementId(idPrefix, 'skeleton-about', 'body')" type="text" :repeat="3" />
        </section>

        <section
          v-if="showAgenda"
          :id="createElementId(idPrefix, 'skeleton', 'agenda')"
          class="flex flex-col overflow-hidden rounded-xl bg-background shadow-sm"
        >
          <div class="flex flex-wrap items-start justify-between gap-3 px-5 py-4 md:px-6">
            <div class="flex min-w-0 flex-col gap-2">
              <CPSkeleton :id="createElementId(idPrefix, 'skeleton-agenda', 'title')" type="text" width="5rem" height="1.5rem" />
              <CPSkeleton :id="createElementId(idPrefix, 'skeleton-agenda', 'summary')" type="text" width="12rem" height="0.875rem" />
            </div>
            <CPSkeleton :id="createElementId(idPrefix, 'skeleton-agenda', 'search')" type="text" width="12rem" height="2rem" />
          </div>
          <div class="h-px bg-[var(--cp-border-500)]" />
          <div class="grid grid-cols-1 lg:grid-cols-[13.75rem_minmax(0,1fr)]">
            <aside class="hidden flex-col gap-2 p-4 lg:flex">
              <CPSkeleton type="text" width="4rem" height="0.75rem" />
              <div v-for="index in 3" :key="index" class="flex flex-col gap-2 rounded-lg px-3 py-2">
                <CPSkeleton type="text" width="70%" height="1rem" />
                <CPSkeleton type="text" width="40%" height="0.75rem" />
              </div>
            </aside>
            <div class="flex flex-col gap-2 px-4 py-4 md:px-6">
              <CPSkeleton class="lg:hidden" type="text" width="100%" height="2rem" />
              <div
                v-for="index in 3"
                :id="createElementId(idPrefix, 'skeleton-session', String(index))"
                :key="index"
                class="flex flex-col gap-3 rounded-[0.625rem] border border-solid border-[var(--cp-border-500)] px-3 py-3.5 sm:flex-row sm:items-start"
              >
                <CPSkeleton type="text" width="4rem" height="1.5rem" />
                <div class="flex min-w-0 flex-1 flex-col gap-2">
                  <CPSkeleton type="text" width="70%" height="1.25rem" />
                  <CPSkeleton type="text" width="50%" height="0.875rem" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
